import { NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import { DateSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form'

import { BaseForm } from '@/components/admin/structure-data/schema/forms/BaseForm'
import {
  DateFormValues,
  dateNullToString,
  dateValidationSchema,
  stringToDateNull,
} from '@/components/admin/structure-data/schema/forms/validation'

export function ItemDateForm({
  item,
  onUpdate,
}: {
  item: DateSchemaItem
  onUpdate: (item: DateSchemaItem) => void
}) {
  const defaultValues = useMemo(
    () =>
      ({
        label: item.label,
        instructionsToFillValue: item.instructionsToFillValue,
        defaultValue: dateNullToString(item.defaultValue),
        type: 'date',
      }) satisfies DateFormValues,
    [item],
  )

  const handleUpdate = (values: DateFormValues) => {
    onUpdate({ ...item, ...values, defaultValue: stringToDateNull(values.defaultValue) })
  }

  return (
    <BaseForm defaultValues={defaultValues} onUpdate={handleUpdate} schema={dateValidationSchema}>
      {renderFields}
    </BaseForm>
  )
}

type FormApi = UseFormReturn<DateFormValues>
function renderFields(formApi: FormApi) {
  return (
    <FormField control={formApi.control} name="defaultValue" render={renderDefaultValueField} />
  )
}

const renderDefaultValueField = ({
  field,
}: {
  field: ControllerRenderProps<DateFormValues, 'defaultValue'>
}) => {
  const { tc } = useT()

  return (
    <FormItem>
      <FormLabel>
        {tc('defaultValue', { cfl: true })} ({tc('optional')})
      </FormLabel>
      <FormControl>
        <div className="flex items-center gap-2">
          <Input
            {...field}
            id="defaultValue"
            placeholder={tc('defaultValue', { cfl: true })}
            type="date"
          />
          <TooltipWrapper content={tc('delete', { cfl: true })}>
            <Button
              variant="outline"
              onClick={() => field.onChange(NULL_AS_STRING)}
              size="icon"
              disabled={field.value === NULL_AS_STRING}
            >
              <Trash2Icon className="size-4" />
            </Button>
          </TooltipWrapper>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
